<template>
	<div class="my-workflows my-workflows_container">
		<div class="my-workflows_header">
			<h5 class="H5-5 my-workflows_title">My Experiments</h5>
			<md-button class="my-workflows_header-btn md-primary md-raised md-button-large" @click="createNewWorkflow">
				<base-icon icon-name="add" />
				New Experiment
			</md-button>
		</div>

		<div class="my-workflows_body" v-if="myWorkflows && myWorkflows.length > 0">
			<div class="my-workflows_sorting">
				<span></span>
				<md-button class="sorting_btn md-dense md-primary" @click="getWorkflowsOrdered">
					Date Modified
					<base-icon
						class="color-inherit"
						:icon-name="descend ? 'arrow-down' : 'arrow-up'"
						icon-size="18px"
					/>
				</md-button>
			</div>
			<div class="my-workflows-list" v-if="myWorkflows">
				<my-workflow
					@viewMyWorkflow="viewMyWorkflow"
					v-for="(workflow, index) in myWorkflows"
					:workflow="workflow"
					:key="index"
				/>
			</div>
			<div class="my-workflows-count">
				<p class="caption-small" v-if="myWorkflows && totalNumOfMyWorkflows">
					{{ setWFViewCount }}
				</p>
				<span></span>
				<md-button
					v-if="totalNumOfMyWorkflows > 10 && myWorkflows && myWorkflows.length !== totalNumOfMyWorkflows"
					class="md-primary md-border-primary md-button-large show-more-btn"
					@click="getMoreWorkflows"
					>Show More Experiments</md-button
				>
			</div>
		</div>

		<div v-else class="no-workflows">
			<div class="no-workflows_message">
				<base-icon icon-name="list" icon-size="36px" />
				<h5>There are no experiments in your account</h5>
				<a href="/workflow-configurator/workflows">
					<h5 class="no-workflows_message-link">Start new experiment</h5>
				</a>
			</div>
		</div>
		<snack-bar />
	</div>
</template>

<script>
import {mapState} from 'vuex';
import callToApiBackend from '@/components/workflow/mixins/callToApiBackend';
import MyWorkflow from '@/components/myWorkflows/MyWorkflow.vue';
import {trackObject, createObject} from '@/services/tracking.js';
import saveToLocalStorage from '@/components/workflow/mixins/saveToLocalStorage';
import {showGlobalBanner} from '@/services/global-banner.js';

export default {
	name: 'MyWorkflows',
	props: {},
	mixins: [callToApiBackend, saveToLocalStorage],
	components: {MyWorkflow},
	provide() {
		return {
			getMyWorkflows: this.getMyWorkflows,
		};
	},
	data() {
		return {
			newWorkflow: false,
			myWFOffset: 10,
			descend: true,
			totalNumOfMyWorkflows: null,
		};
	},
	computed: {
		...mapState(['myWorkflows']),
		setWFViewCount() {
			// get total count of workflows
			return `You've viewed ${this.myWorkflows.length} of ${this.totalNumOfMyWorkflows} experiments`;
		},
		getWorkflowsEndpoint() {
			return this.$store.state.workflowsEndpoint;
		},
	},
	methods: {
		async getMyWorkflows() {
			this.myWFOffset = 10;
			const getWorkflows = await this.callToApiMyWorkflow(`${this.getWorkflowsEndpoint}?limit=10`, 'get');
			if (!getWorkflows) {
				showGlobalBanner(this, {
					title: 'Experiments list could not be loaded',
					body: 'Due to technical reasons we were not able to load your experiments. Please try again.',
					btn: 'dismiss',
				});
				return;
			}
			if (getWorkflows.total_workflows) this.totalNumOfMyWorkflows = getWorkflows.total_workflows;
			const formattedWF = this.formatWorkflows(getWorkflows.workflows);
			this.$store.commit('setMyWorkflows', formattedWF);
		},
		async getMoreWorkflows() {
			const order = !this.descend ? 'ASC' : 'DESC';
			const moreWF = await this.callToApiMyWorkflow(
				`${this.getWorkflowsEndpoint}?limit=10&offset=${this.myWFOffset + 1}&order=${order}`,
			);
			// update offest by 10
			this.myWFOffset += 10;
			if (!moreWF) return;
			const workflowsDifference = moreWF.total_workflows - this.totalNumOfMyWorkflows;
			this.totalNumOfMyWorkflows = moreWF.total_workflows;

			const slicing = this.descend ? workflowsDifference : 0;
			const formattedWF = await this.formatWorkflows(moreWF.workflows.slice(slicing));
			// append new workflows to myWorkflows in store
			this.$store.commit('setAppendMyWorkflows', formattedWF);

			if (workflowsDifference > 0 && this.descend) {
				const startWF = await this.callToApiMyWorkflow(
					`${this.getWorkflowsEndpoint}?limit=${workflowsDifference}&order=${order}`,
				);
				if (!startWF) return;

				const formattedStartWF = await this.formatWorkflows(startWF.workflows);
				this.$store.commit('setAppendToBeginMyWorkflows', formattedStartWF);
			}
		},
		async getWorkflowsOrdered() {
			// get the amount of wf already displayed
			const limit = this.myWFOffset;
			// set the opposite order of whats current
			const order = this.descend ? 'ASC' : 'DESC';
			const myWorkflows = await this.callToApiMyWorkflow(
				`${this.getWorkflowsEndpoint}?limit=${limit}&order=${order}`,
			);
			if (!myWorkflows) return;
			this.totalNumOfMyWorkflows = myWorkflows.total_workflows;
			// flip the stored order
			this.descend = !this.descend;
			const formattedWF = await this.formatWorkflows(myWorkflows.workflows);
			this.$store.commit('setMyWorkflows', formattedWF);
		},
		formatWorkflows(workflows) {
			return workflows.map((workflow) => ({
				id: workflow.id,
				title: workflow.title,
				data: workflow.document,
			}));
		},
		createNewWorkflow() {
			const selection = null;
			['analyte', 'application', 'startingMaterial', 'technology'].forEach((category) => {
				this.$store.commit('setCategorySelection', {category, selection});
				this.$router.replace({name: 'Home', query: {}}).catch((err) => err);
			});
		},
		async viewMyWorkflow(workflow) {
			// set the workflow data to store
			const lastModDate = new Date(workflow.data.last_mod_date);
			await this.$store.commit('setLastModDate', lastModDate.toLocaleDateString());
			await this.$store.commit('setWorkFlowId', workflow.id);
			await this.$store.commit('setWorkflowHeadline', workflow.title);
			await this.$store.commit('setMyWorkflow', workflow);
			if (localStorage.getItem('shareOnReturn')) {
				await this.$store.commit('setEnableShareUrl', true);
			}
			await this.$nextTick();
		},
		checkIfSentToLogin() {
			if (localStorage.getItem('sentToLogin') && this.$store.state.jwt) {
				document.cookie = `COUNTRY=${this.$store.state.jwt.country}; domain=.qiagen.com; expires=${new Date(
					2147483647 * 1000,
				).toUTCString()}; path=/; secure`;
				localStorage.removeItem('sentToLogin');
			}
		},
		getLoggedInStatus() {
			const jwt = this.$store.state.jwt;
			if (jwt) return `${jwt.lastName}, ${jwt.firstName}`;
			return null;
		},
		getCountry() {
			return this.$store.state.jwt ? this.$store.state.jwt.country : this.$store.state.COUNTRY;
		},
		getUserId() {
			const jwt = this.$store.state.jwt;
			if (jwt) return jwt.sub;
			return 'not loggedin';
		},
		sendToLogin() {
			// TBD: move to mixins
			const loginLink = this.$store.state.loginLink;
			let view = `${window.location.origin}/workflow-configurator/myWorkflows`;

			window.location = `${loginLink}${view}`;

			this.$emit('sentToLogin');
		},
	},
	async created() {
		const isLoggedIn = this.getLoggedInStatus();

		this.$root.$emit('closeGlobalBanner');
		await this.$store.dispatch('fetchUrls');

		if (!isLoggedIn) {
			return this.sendToLogin();
		}

		await this.$store.dispatch('fetchHelperFiles');
		await this.$store.dispatch('fetchCategories');
		await this.$store.dispatch('checkPublicationIds');

		const s_dtm = createObject('my workflows', this.getCountry(), isLoggedIn, this.getUserId());
		trackObject(s_dtm);

		await this.getMyWorkflows();
	},
	updated() {
		this.$nextTick(() => {
			this.saveMyWorkflowsToLocalStorage(this.myWorkflows);
		});
	},
};
</script>

<style lang="scss" scoped>
.md-snackbar.md-position-left {
	@media (max-width: $mobile) {
		bottom: 76px;
		left: 10px;
	}
}

.my-workflows_container {
	padding-top: 12px;
	@media (min-width: $tablet) {
		padding-top: 22px;
	}
}
.my-workflows_header {
	margin-bottom: 32px;
	@media (min-width: $mobile) {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 36px;
	}
}
.my-workflows_title {
	margin-bottom: 26px;
	@media (min-width: $mobile) {
		margin: 0;
	}
}
.my-workflows_header-btn {
	width: 100%;
	@media (min-width: $mobile) {
		width: auto;
	}
}
.my-workflows_sorting {
	display: flex;
	align-items: center;
	justify-content: space-between;
	border-bottom: 1px solid $color-mid-grey;
	padding-bottom: 6px;
	margin-bottom: 18px;
	@media (min-width: $tablet) {
		margin-bottom: 24px;
	}
}
.sorting_btn .md-ripple {
	padding: 0 !important;
	background-color: transparent;
}
.no-workflows {
	display: flex;
	align-items: center;
	justify-content: center;
	margin-top: 26px;
	border-top: 1px solid $color-mid-grey;
	text-align: center;
}
.no-workflows_message {
	margin-top: 150px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 500px;
	.icon {
		margin-bottom: 30px;
	}
}
.no-workflows_message-link {
	color: $color-blue;
}
.my-workflows-count {
	text-align: center;
	margin-bottom: 112px;
}
</style>
